import * as Popover from '@radix-ui/react-popover'
import { ArrowLineRight, Cards, Copy, MagnifyingGlass, Plus, PushPin, PushPinSlash, X, XSquare, type Icon as PhIcon } from '@phosphor-icons/react'
import { useIsFetching } from '@tanstack/react-query'
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { cn, Kbd, Tooltip } from '@/design-system'
import { useT } from '@/i18n'
import { useTabs, type WorkspaceTab } from '@/stores/tabs'
import { useTabMeta, tabMeta, type TabMeta } from './tabMeta'
import { useTabActivity } from './Workspace'

export const shownUrl = (url: string) => {
  try {
    return decodeURI(url)
  } catch {
    return url
  }
}

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Requests slower than 250ms light the front tab's live dot (cached data never flickers it). */
function useSlowFetching() {
  const fetching = useIsFetching()
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!fetching) return setBusy(false)
    const t = setTimeout(() => setBusy(true), 250)
    return () => clearTimeout(t)
  }, [fetching])
  return busy
}

/** Collapse the tab first, then drop it from the store (the neighbours glide into its place). */
function closeAnimated(id: string, el: HTMLElement | undefined) {
  const { close } = useTabs.getState()
  if (!el || reducedMotion()) return close(id)
  el.style.maxWidth = `${el.offsetWidth}px`
  el.dataset.closing = 'true'
  setTimeout(() => close(id), 170)
}

export function TabStrip({ onOpenCommand }: { onOpenCommand: () => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const tabs = useTabs((s) => s.tabs)
  const activeId = useTabs((s) => s.activeId)
  const loading = useTabActivity((s) => s.loading)
  const busy = useSlowFetching()
  const scroller = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const els = useRef(new Map<string, HTMLDivElement>())
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null)
  const [fade, setFade] = useState({ left: false, right: false })
  const [bar, setBar] = useState<{ left: number; right: number; forward: boolean } | null>(null)
  const drag = useRef<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)

  const register = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) els.current.set(id, el)
    else els.current.delete(id)
  }, [])

  /* fade edges: which physical side still has tabs out of view (RTL scrollLeft runs 0 → negative) */
  const measureFade = useCallback(() => {
    const s = scroller.current
    if (!s) return
    const max = s.scrollWidth - s.clientWidth
    const x = Math.abs(s.scrollLeft)
    const rtl = getComputedStyle(s).direction === 'rtl'
    const fromStart = x > 1
    const beforeEnd = x < max - 1
    setFade(rtl ? { right: fromStart, left: beforeEnd } : { left: fromStart, right: beforeEnd })
  }, [])

  /* the glowing underline: glides to the front tab, leading edge first (a little comet) */
  const measureBar = useCallback(() => {
    const el = els.current.get(useTabs.getState().activeId)
    const l = list.current
    if (!el || !l) return setBar(null)
    const inset = el.offsetWidth > 60 ? 12 : 8
    const left = el.offsetLeft + inset
    const right = l.clientWidth - (el.offsetLeft + el.offsetWidth) + inset
    setBar((prev) => (prev && prev.left === left && prev.right === right ? prev : { left, right, forward: !prev || left > prev.left }))
  }, [])

  useLayoutEffect(() => {
    measureBar()
    measureFade()
  }, [tabs, activeId, measureBar, measureFade, locale])

  useEffect(() => {
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => (measureBar(), measureFade())) : null
    if (list.current) ro?.observe(list.current)
    if (scroller.current) ro?.observe(scroller.current)
    return () => ro?.disconnect()
  }, [measureBar, measureFade])

  // bring the front tab into view
  useEffect(() => {
    els.current.get(activeId)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' })
  }, [activeId])

  /* reorder animation (FLIP): only when the same tabs changed places — opening and closing animate themselves */
  const order = tabs.map((t) => t.id).join('|')
  const lastPos = useRef<{ order: string; pos: Map<string, number> }>({ order: '', pos: new Map() })
  useLayoutEffect(() => {
    const pos = new Map<string, number>()
    els.current.forEach((el, id) => pos.set(id, el.offsetLeft))
    const prev = lastPos.current
    const sameSet = prev.order.split('|').sort().join('|') === order.split('|').sort().join('|')
    if (sameSet && prev.order !== order && !reducedMotion()) {
      els.current.forEach((el, id) => {
        const from = prev.pos.get(id)
        const to = pos.get(id)
        if (from === undefined || to === undefined || from === to || typeof el.animate !== 'function') return
        el.animate([{ transform: `translateX(${from - to}px)` }, { transform: 'translateX(0)' }], { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' })
      })
    }
    lastPos.current = { order, pos }
  }, [order])

  const onWheel = (e: React.WheelEvent) => {
    const s = scroller.current
    if (!s || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
    const rtl = getComputedStyle(s).direction === 'rtl'
    s.scrollBy({ left: rtl ? -e.deltaY : e.deltaY })
  }

  /* keyboard inside the strip: arrows walk the tabs (mirrored in RTL), Delete closes */
  const onListKey = (e: ReactKeyboardEvent) => {
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>('.ws-tab-main') ?? [])]
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (i < 0) return
    const rtl = document.documentElement.dir === 'rtl'
    const step = e.key === 'ArrowRight' ? (rtl ? -1 : 1) : e.key === 'ArrowLeft' ? (rtl ? 1 : -1) : 0
    if (step) {
      e.preventDefault()
      buttons[(i + step + buttons.length) % buttons.length]?.focus()
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      buttons[e.key === 'Home' ? 0 : buttons.length - 1]?.focus()
    } else if (e.key === 'Delete') {
      const id = buttons[i]!.closest<HTMLElement>('[data-tab-id]')?.dataset.tabId
      if (id) closeAnimated(id, els.current.get(id))
    }
  }

  const onDragOverTab = (e: React.DragEvent, overId: string) => {
    const dragId = drag.current
    if (!dragId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragId === overId) return
    const { tabs: now, move } = useTabs.getState()
    const from = now.findIndex((t) => t.id === dragId)
    const to = now.findIndex((t) => t.id === overId)
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const mid = r.left + r.width / 2
    const rtl = document.documentElement.dir === 'rtl'
    const forward = from < to
    // only swap once the pointer passes the middle of the tab it is over (no flip-flopping between two tabs)
    const past = rtl ? (forward ? e.clientX < mid : e.clientX > mid) : forward ? e.clientX > mid : e.clientX < mid
    if (past) move(dragId, to)
  }

  const pinnedCount = tabs.filter((t) => t.pinned).length

  return (
    <div className="ws-strip hidden md:flex" aria-label={ar ? 'علامات التبويب المفتوحة' : 'Open tabs'} role="navigation">
      <div
        ref={scroller}
        className="ws-scroller"
        onScroll={measureFade}
        onWheel={onWheel}
        style={{ '--fade-l': fade.left ? '36px' : '0px', '--fade-r': fade.right ? '36px' : '0px' } as CSSProperties}
      >
        <div ref={list} className="ws-list" onKeyDown={onListKey}>
          {tabs.map((t, i) => (
            <TabItem
              key={t.id}
              tab={t}
              active={t.id === activeId}
              live={Boolean(loading[t.id]) || (t.id === activeId && busy)}
              dragging={dragging === t.id}
              groupEnd={t.pinned && i === pinnedCount - 1 && pinnedCount < tabs.length}
              register={register}
              onContext={(x, y) => setMenu({ id: t.id, x, y })}
              onDragStart={() => ((drag.current = t.id), setDragging(t.id))}
              onDragEnd={() => ((drag.current = null), setDragging(null))}
              onDragOver={(e) => onDragOverTab(e, t.id)}
            />
          ))}
          {bar && (
            <span
              className="ws-indicator"
              aria-hidden
              style={{
                left: bar.left,
                right: bar.right,
                // the edge that leads the move goes first, the trailing edge catches up
                transition: bar.forward ? 'right 240ms cubic-bezier(.2,.9,.3,1), left 380ms cubic-bezier(.2,.9,.3,1) 40ms' : 'left 240ms cubic-bezier(.2,.9,.3,1), right 380ms cubic-bezier(.2,.9,.3,1) 40ms',
              }}
            />
          )}
        </div>
      </div>

      <div className="ws-actions">
        <Tooltip content={<span className="flex items-center gap-1.5">{ar ? 'فتح صفحة في علامة تبويب' : 'Open a page in a tab'} <Kbd>Ctrl</Kbd><Kbd>K</Kbd></span>}>
          <button type="button" className="ws-action" onClick={onOpenCommand} aria-label={ar ? 'علامة تبويب جديدة' : 'New tab'}>
            <Plus size={16} weight="bold" aria-hidden />
          </button>
        </Tooltip>
        <QuickSwitcher />
      </div>

      {menu && <TabContextMenu {...menu} onClose={() => setMenu(null)} closeAnimated={(id) => closeAnimated(id, els.current.get(id))} />}
    </div>
  )
}

interface TabItemProps {
  tab: WorkspaceTab
  active: boolean
  live: boolean
  dragging: boolean
  groupEnd: boolean
  register: (id: string, el: HTMLDivElement | null) => void
  onContext: (x: number, y: number) => void
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: (e: React.DragEvent) => void
}

const TabItem = memo(function TabItem({ tab, active, live, dragging, groupEnd, register, onContext, onDragStart, onDragEnd, onDragOver }: TabItemProps) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const meta = useTabMeta(tab.url)
  const ref = useRef<HTMLDivElement | null>(null)
  const I = meta.icon
  return (
    <div
      ref={(el) => ((ref.current = el), register(tab.id, el))}
      data-tab-id={tab.id}
      data-active={active}
      data-pinned={tab.pinned}
      data-dragging={dragging || undefined}
      data-group-end={groupEnd || undefined}
      className="ws-tab"
      style={{ '--hue': meta.hue } as CSSProperties}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', location.origin + tab.url)
        onDragStart()
      }}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={(e) => e.preventDefault()}
      onContextMenu={(e) => {
        e.preventDefault()
        onContext(e.clientX, e.clientY)
      }}
      onMouseDown={(e) => e.button === 1 && e.preventDefault()}
      onAuxClick={(e) => {
        if (e.button !== 1) return
        e.preventDefault()
        closeAnimated(tab.id, ref.current ?? undefined)
      }}
    >
      <Tooltip content={<TabTooltip meta={meta} url={tab.url} pinned={tab.pinned} ar={ar} />} side="bottom">
        <button type="button" className="ws-tab-main" aria-current={active ? 'page' : undefined} onClick={() => useTabs.getState().focus(tab.id)}>
          <span className="ws-tab-icon">
            <I size={15} weight={active ? 'fill' : 'duotone'} aria-hidden />
            {live && <span className="ws-live" aria-label={ar ? 'جار التحميل' : 'Loading'} />}
          </span>
          {tab.pinned ? (
            <span className="sr-only">{meta.title}</span>
          ) : (
            <span className="ws-tab-title">
              {meta.title}
              {meta.hint && <span className="ws-tab-hint" dir="auto">{meta.hint}</span>}
            </span>
          )}
        </button>
      </Tooltip>
      {!tab.pinned && (
        <button type="button" className="ws-tab-close" tabIndex={-1} aria-label={ar ? `إغلاق ${meta.title}` : `Close ${meta.title}`} onClick={() => closeAnimated(tab.id, ref.current ?? undefined)}>
          <X size={11} weight="bold" aria-hidden />
        </button>
      )}
    </div>
  )
})

function TabTooltip({ meta, url, pinned, ar }: { meta: TabMeta; url: string; pinned: boolean; ar: boolean }) {
  return (
    <span className="block max-w-72 space-y-0.5 py-0.5">
      <span className="flex items-center gap-1.5 font-semibold">
        {pinned && <PushPin size={11} weight="fill" aria-hidden />}
        {meta.title}
        {meta.hint && <span className="opacity-70">· {meta.hint}</span>}
      </span>
      {meta.section && <span className="block text-2xs opacity-70">{meta.section}</span>}
      <span className="block truncate font-mono text-2xs opacity-60" dir="ltr">{shownUrl(url)}</span>
      <span className="block pt-0.5 text-2xs opacity-60">{ar ? 'زر الماوس الأوسط للإغلاق · النقر الأيمن لمزيد من الخيارات' : 'Middle-click closes · right-click for more'}</span>
    </span>
  )
}

/* ---------------------------------------------------------------- right-click menu */

function TabContextMenu({ id, x, y, onClose, closeAnimated: closeOne }: { id: string; x: number; y: number; onClose: () => void; closeAnimated: (id: string) => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const tabs = useTabs((s) => s.tabs)
  const tab = tabs.find((t) => t.id === id)
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: x, top: y })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    const rtl = document.documentElement.dir === 'rtl'
    // RTL: the menu opens towards the left of the pointer, like the rest of the Arabic UI
    let left = rtl ? x - w : x
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8))
    const top = Math.max(8, Math.min(y, window.innerHeight - h - 8))
    setPos({ left, top })
    el.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus()
  }, [x, y])

  useEffect(() => {
    const away = (e: Event) => !ref.current?.contains(e.target as Node) && onClose()
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', key)
    window.addEventListener('resize', onClose)
    window.addEventListener('blur', onClose)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', key)
      window.removeEventListener('resize', onClose)
      window.removeEventListener('blur', onClose)
    }
  }, [onClose])

  if (!tab) return null
  const i = tabs.indexOf(tab)
  const s = useTabs.getState()
  const after = tabs.slice(i + 1).filter((t) => !t.pinned).length
  const others = tabs.filter((t) => t.id !== id && !t.pinned).length
  const items: ({ label: string; icon: PhIcon; run: () => void; disabled?: boolean; danger?: boolean; flip?: boolean } | 'sep')[] = [
    { label: tab.pinned ? (ar ? 'إلغاء التثبيت' : 'Unpin') : ar ? 'تثبيت علامة التبويب' : 'Pin tab', icon: tab.pinned ? PushPinSlash : PushPin, run: () => s.togglePin(id) },
    { label: ar ? 'تكرار علامة التبويب' : 'Duplicate', icon: Copy, run: () => s.duplicate(id) },
    'sep',
    { label: ar ? 'إغلاق' : 'Close', icon: X, run: () => closeOne(id) },
    { label: ar ? 'إغلاق الباقي' : 'Close others', icon: XSquare, run: () => s.closeOthers(id), disabled: !others },
    // "after" = to the right in English, to the left in Arabic: the arrow mirrors with the layout
    { label: ar ? 'إغلاق ما بعدها' : 'Close tabs to the right', icon: ArrowLineRight, run: () => s.closeAfter(id), disabled: !after, flip: true },
  ]

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const btns = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])]
    const at = btns.indexOf(document.activeElement as HTMLButtonElement)
    btns[(at + (e.key === 'ArrowDown' ? 1 : -1) + btns.length) % btns.length]?.focus()
  }

  return createPortal(
    <div ref={ref} role="menu" className="ws-menu animate-scale-in" style={{ left: pos.left, top: pos.top }} onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()}>
      {items.map((it, k) =>
        it === 'sep' ? (
          <div key={k} className="my-1 h-px bg-border" role="separator" />
        ) : (
          <button
            key={it.label}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            className="ws-menu-item"
            onClick={() => {
              onClose()
              it.run()
            }}
          >
            <it.icon size={15} className={cn('text-fg-muted', it.flip && 'rtl:-scale-x-100')} aria-hidden />
            {it.label}
          </button>
        ),
      )}
    </div>,
    document.body,
  )
}

/* ---------------------------------------------------------------- quick switcher (all tabs, searchable) */

const normalise = (s: string) => s.toLowerCase().replace(/[\u064B-\u065F\u0670]/g, '').replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')

function QuickSwitcher() {
  const { locale, l } = useT()
  const ar = locale === 'ar'
  const tabs = useTabs((s) => s.tabs)
  const activeId = useTabs((s) => s.activeId)
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [index, setIndex] = useState(0)
  const rows = useMemo(() => {
    const all = tabs.map((t) => ({ tab: t, meta: tabMeta(t.url, l) }))
    const nq = normalise(q.trim())
    const hit = nq ? all.filter(({ tab, meta }) => [meta.title, meta.hint, meta.section, shownUrl(tab.url)].some((h) => normalise(h).includes(nq))) : all
    // most recently used first: the tab you just left is always one keystroke away
    return hit.sort((a, b) => b.tab.lastUsed - a.tab.lastUsed)
  }, [tabs, q, l])

  useEffect(() => setIndex(0), [q, open])
  useEffect(() => {
    if (!open) setQ('')
  }, [open])

  const pick = (id: string) => {
    useTabs.getState().focus(id)
    setOpen(false)
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip content={ar ? 'كل علامات التبويب' : 'All tabs'}>
        <Popover.Trigger asChild>
          <button type="button" className="ws-action ws-count" aria-label={ar ? `كل علامات التبويب (${tabs.length})` : `All tabs (${tabs.length})`}>
            <Cards size={16} weight="duotone" aria-hidden />
            <span className="tabular">{tabs.length}</span>
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8} className="z-50 w-[340px] overflow-hidden rounded-xl border border-border bg-surface-raised shadow-lg data-[state=open]:animate-scale-in">
          <div className="flex items-center gap-2 border-b border-border px-3">
            <MagnifyingGlass size={15} className="text-fg-subtle" aria-hidden />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={ar ? 'ابحث في علامات التبويب المفتوحة…' : 'Search open tabs…'}
              aria-label={ar ? 'البحث في علامات التبويب' : 'Search tabs'}
              className="h-11 flex-1 bg-transparent text-sm outline-none placeholder:text-fg-subtle"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') (e.preventDefault(), setIndex((i) => Math.min(i + 1, rows.length - 1)))
                if (e.key === 'ArrowUp') (e.preventDefault(), setIndex((i) => Math.max(i - 1, 0)))
                if (e.key === 'Enter' && rows[index]) pick(rows[index]!.tab.id)
              }}
            />
            <span className="text-2xs text-fg-subtle tabular">{rows.length}/{tabs.length}</span>
          </div>
          <ul className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5" role="listbox" aria-label={ar ? 'علامات التبويب' : 'Tabs'}>
            {rows.length === 0 && <li className="px-3 py-8 text-center text-sm text-fg-muted">{ar ? 'لا توجد علامة تبويب بهذا الاسم' : 'No tab matches'}</li>}
            {rows.map(({ tab, meta }, i) => {
              const I = meta.icon
              return (
                <li key={tab.id} role="option" aria-selected={i === index} className="group">
                  <div className={cn('flex items-center gap-2.5 rounded-lg px-2 py-1.5', i === index && 'bg-surface-hover')} onMouseEnter={() => setIndex(i)}>
                    <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-start" onClick={() => pick(tab.id)}>
                      <span className="ws-tab-icon" style={{ '--hue': meta.hue, width: 32, height: 32, borderRadius: 9 } as CSSProperties}>
                        <I size={16} weight="duotone" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-fg-strong">
                          <span className="truncate">{meta.title}</span>
                          {meta.hint && <span className="shrink-0 text-xs font-medium text-fg-muted">· {meta.hint}</span>}
                          {tab.pinned && <PushPin size={11} weight="fill" className="shrink-0 text-fg-subtle" aria-label={ar ? 'مثبت' : 'Pinned'} />}
                          {tab.id === activeId && <span className="shrink-0 rounded-full bg-[#AE1A1C]/10 px-1.5 text-2xs font-semibold text-[#AE1A1C]">{ar ? 'المفتوحة الآن' : 'Current'}</span>}
                        </span>
                        <span className="block truncate font-mono text-2xs text-fg-subtle" dir="ltr">{shownUrl(tab.url)}</span>
                      </span>
                    </button>
                    {!tab.pinned && (
                      <button type="button" className="rounded-md p-1 text-fg-subtle opacity-0 hover:bg-surface-active hover:text-fg group-hover:opacity-100 focus-visible:opacity-100" aria-label={ar ? `إغلاق ${meta.title}` : `Close ${meta.title}`} onClick={() => useTabs.getState().close(tab.id)}>
                        <X size={13} weight="bold" aria-hidden />
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-bg-subtle px-3 py-2 text-2xs text-fg-muted">
            <span className="flex items-center gap-1"><Kbd>Ctrl</Kbd><Kbd>Tab</Kbd> {ar ? 'التالي' : 'next'}</span>
            <span className="flex items-center gap-1"><Kbd>Alt</Kbd><Kbd>1-9</Kbd> {ar ? 'علامة تبويب برقمها' : 'by number'}</span>
            <span className="flex items-center gap-1"><Kbd>Alt</Kbd><Kbd>W</Kbd> {ar ? 'إغلاق' : 'close'}</span>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
